import { Loader2 } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router"

import { NewPasswordFields } from "@/components/domain/new-password-fields"
import { newPasswordReady } from "@/components/domain/password-rules"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

import { AuthLayout } from "./auth-layout"
import { useCurrentUser, useResetPasswordMutation, type ResetPasswordError } from "./auth-store"

export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  const reset = useResetPasswordMutation()
  const currentUser = useCurrentUser()

  // A signed-in reviewer who lands here with a token is completing a reset for
  // *their* mailbox — accepting will replace the current session, so let them
  // through. Without a token there is nothing to complete; bounce to /reviews.
  React.useEffect(() => {
    if (currentUser && !token) navigate("/reviews", { replace: true })
  }, [currentUser, token, navigate])

  const [password, setPassword] = React.useState("")
  const [confirm, setConfirm] = React.useState("")
  const ready = newPasswordReady(password, confirm)

  if (!token) {
    return (
      <AuthLayout
        eyebrow="Reset password"
        title="Reset link missing"
        subtitle="This page expects a reset token in the URL."
        footer={
          <p>
            <Link to="/forgot-password" className="text-brand hover:underline">
              Request a new link
            </Link>
          </p>
        }
      >
        <Alert tone="error" size="sm" title="No reset token">
          Open the link from your email, or request a new one.
        </Alert>
      </AuthLayout>
    )
  }

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (!ready || reset.isPending) return
    try {
      await reset.mutateAsync({ token, newPassword: password })
      navigate("/reviews", { replace: true })
    } catch {
      /* surfaced via reset.error */
    }
  }

  const topError = toTopError(reset.error)
  const weakPassword = reset.error?.kind === "weak_password" ? reset.error.message : null
  const edit = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    // Only a refusal this edit can fix. A dead link cannot be typed away, and that alert is
    // where "Request a new link" lives — dismissing it would hide the way out.
    if (reset.error?.kind === "weak_password") reset.reset()
  }

  return (
    <AuthLayout
      eyebrow="Reset password"
      title="Choose a new password"
      subtitle="Pick something you haven't used before. Any active sessions on other devices will be signed out."
      footer={
        <p>
          <Link to="/login" className="text-text-2 hover:text-text">
            Back to sign in
          </Link>
        </p>
      }
    >
      <form
        onSubmit={onSubmit}
        className="space-y-4"
        noValidate
        aria-busy={reset.isPending || undefined}
      >
        <div className="sr-only" aria-live="polite" role="status">
          {reset.isPending ? "Setting your new password…" : topError ? topError.body : ""}
        </div>

        {topError && (
          <Alert tone="error" size="sm" title={topError.title}>
            {topError.body}
            {topError.canRequestNew && (
              <>
                {" "}
                <Link to="/forgot-password" className="underline">
                  Request a new link
                </Link>
                .
              </>
            )}
          </Alert>
        )}

        {currentUser && (
          <Alert tone="warning" size="sm" title={`Signed in as ${currentUser.email}`}>
            Completing this reset will replace your current session.
          </Alert>
        )}

        <NewPasswordFields
          password={password}
          onPasswordChange={edit(setPassword)}
          confirmation={confirm}
          onConfirmationChange={edit(setConfirm)}
          serverError={weakPassword}
          inputSize="lg"
          disabled={reset.isPending}
          autoFocus
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          leadingIcon={reset.isPending ? <Loader2 className="animate-spin" /> : undefined}
          disabled={!ready || reset.isPending}
        >
          {reset.isPending ? "Setting new password…" : "Set new password and sign in"}
        </Button>
      </form>
    </AuthLayout>
  )
}

interface TopError {
  title: string
  body: string
  canRequestNew: boolean
}

function toTopError(error: ResetPasswordError | null): TopError | null {
  if (!error) return null
  switch (error.kind) {
    case "weak_password":
      // Shown on the password field, where the fix is.
      return null
    case "invalid_or_expired":
      return {
        title: "Reset link invalid or expired",
        body: "This link no longer works.",
        canRequestNew: true,
      }
    case "network":
      return {
        title: "Network problem",
        body: "We couldn't reach the server. Check your connection and try again.",
        canRequestNew: false,
      }
    case "server":
      return {
        title: "Server error",
        body: "Something went wrong on our side. Please try again in a moment.",
        canRequestNew: false,
      }
    default:
      return { title: "Could not reset password", body: error.message, canRequestNew: false }
  }
}
